/**
 * @fileoverview A program viewer's Calendar tab: the program's class meetings,
 * opening on this week.
 *
 * The same MeetingCalendar as the organization's Calendar and one class's own,
 * so the three never drift apart: week, month or list, coloured by class here
 * because every meeting is one program's. Read from
 * `GET /org/viewer/programs/{id}/calendar?start=&end=`, the org calendar's
 * meetings cut to this program. Cancelled and covered are said in words, never
 * in colour alone. A meeting opens its details (MeetingDetailsDialog) and
 * nothing more: the viewer changes nothing and has no class page to open.
 */

import { useContext, useEffect, useMemo, useState } from "react";
import { OrgAuthContext } from "@/App";
import orgApi from "@/lib/orgApi";
import { calendarSyncFlags } from "@/lib/featureFlags";
import { CalendarSyncButton } from "@/components/settings/CalendarSyncSection";
import { zoneAbbreviation } from "@/lib/schedule";
import MeetingCalendar, { SWATCH, rangeFor, weekStart } from "@/components/org/MeetingCalendar";
import MeetingDetailsDialog, { calendarMeeting } from "@/components/org/MeetingDetailsDialog";

/** The viewer's own zone: what an unlabelled time reads as when the
    organization has none of its own. */
const viewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const select =
  "rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

/** @param {{ programId: string, classes: Array<{class_id: string, name: string}> }} props */
export default function ViewerCalendar({ programId, classes = [] }) {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => weekStart(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classId, setClassId] = useState("all");
  const [picked, setPicked] = useState(null);
  const { flags } = useContext(OrgAuthContext) || {};
  const syncable = calendarSyncFlags({ flags });
  const { start, end } = rangeFor(view, anchor);

  useEffect(() => {
    let live = true;
    setLoading(true);
    orgApi
      .get(`/org/viewer/programs/${programId}/calendar?start=${start}&end=${end}`)
      .then((res) => live && setData(res.data))
      .catch(() => live && setData({ meetings: [], failed: true }))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [programId, start, end]);

  // As the org Calendar does: the calendar's own zone is named once above it
  // and goes unsaid on each meeting, so only a meeting in another zone is
  // labelled. A coordinator elsewhere reads the program's times as the program's.
  const ownZone = data?.timezone || viewerZone();
  const events = useMemo(
    () =>
      (data?.meetings || [])
        .filter((m) => classId === "all" || m.class_id === classId)
        .map((m) => ({
          id: m.session_id || `${m.class_id}-${m.date}-${m.time_local}`,
          date: m.date,
          time_local: m.time_local,
          timezone: m.timezone && m.timezone !== ownZone ? zoneAbbreviation(m.timezone, m.date) : undefined,
          duration_minutes: m.duration_minutes,
          title: m.class_name,
          subtitle: [m.substitute_name || m.instructor_name, m.room].filter(Boolean).join(" · "),
          color: SWATCH[m.colour] || "var(--accent-yellow)",
          muted: m.status === "cancelled",
          tags: [
            m.status === "cancelled" ? { label: "No class" } : null,
            m.substitute_name ? { label: "Substitute", accent: true } : null,
          ].filter(Boolean),
          onPress: () => setPicked(m),
        })),
    [data, classId, ownZone],
  );

  return (
    <div data-testid="viewer-calendar">
      {(classes.length > 1 || data?.timezone || syncable.link || syncable.google) && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          {classes.length > 1 && (
            <select value={classId} onChange={(e) => setClassId(e.target.value)} aria-label="Filter by class" data-testid="viewer-calendar-class" className={select}>
              <option value="all">All classes</option>
              {classes.map((c) => (
                <option key={c.class_id} value={c.class_id}>{c.name}</option>
              ))}
            </select>
          )}
          {data?.timezone && (
            <span className="font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="viewer-calendar-timezone">
              Times are {data.timezone.replaceAll("_", " ")} unless marked.
            </span>
          )}
          {/* A viewer has no Settings page, so Sync to your calendar always opens the dialog. */}
          {(syncable.link || syncable.google) && (
            <span className="ml-auto">
              <CalendarSyncButton portal="viewer" client={orgApi} prefix={`/org/viewer/programs/${programId}`} available={syncable} />
            </span>
          )}
        </div>
      )}
      {data?.complete === false && (
        <p role="status" className="mb-3 font-sans text-[13px] text-[var(--text-secondary)]">
          Some class schedules are unavailable. This calendar is incomplete; refresh to try again.
        </p>
      )}
      <MeetingCalendar
        events={events}
        view={view}
        onViewChange={setView}
        anchor={anchor}
        onAnchorChange={setAnchor}
        loading={loading}
        emptyText={data?.failed ? "Couldn't load the calendar" : data?.complete === false ? "No meetings loaded" : "Nothing scheduled"}
        onOpenDay={(key) => {
          const [y, m, d] = key.split("-").map(Number);
          setAnchor(new Date(y, m - 1, d));
          setView("week");
        }}
      />
      <MeetingDetailsDialog
        key={picked ? picked.session_id || `${picked.class_id}-${picked.date}-${picked.time_local}` : "none"}
        meeting={
          picked && calendarMeeting(picked)
        }
        onClose={() => setPicked(null)}
      />
    </div>
  );
}
